'use client';

import React from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SVENSKA_MÅNADER } from 'src/sections/moms/momsPeriod';

// ----------------------------------------------------------------------
// Formatters
// ----------------------------------------------------------------------

export const formatKr = (n: number) =>
    `${n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;

export const formatSignedKr = (n: number) => {
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    return `${sign}${Math.abs(n).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
};

export const formatRelativeSync = (iso?: string) => {
    if (!iso) return 'Aldrig';
    const then = dayjs(iso);
    if (!then.isValid()) return 'Aldrig';
    const now = dayjs();
    const diffMin = now.diff(then, 'minute');
    if (diffMin < 1) return 'Just nu';
    if (diffMin < 60) return `${diffMin} min sedan`;
    const diffH = now.diff(then, 'hour');
    if (diffH < 24) return `${diffH} tim sedan`;
    const diffD = now.diff(then, 'day');
    if (diffD < 7) return `${diffD} d sedan`;
    return then.format('D MMM');
};

export const formatRelativeReceived = (iso?: string) => {
    if (!iso) return '';
    const then = dayjs(iso);
    if (!then.isValid()) return '';
    const now = dayjs();
    if (then.isSame(now, 'day')) return `Idag ${then.format('HH:mm')}`;
    if (then.isSame(now.subtract(1, 'day'), 'day')) return `Igår ${then.format('HH:mm')}`;
    return then.format('D MMM');
};

export const toDayjsSafe = (d: any) => {
    if (!d) return null;
    if (typeof d?.format === 'function') return d;
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed : null;
};

export const formatÅrMånad = (d: any) => {
    const day = toDayjsSafe(d);
    return day ? `${day.year()} ${SVENSKA_MÅNADER[day.month()]}` : undefined;
};

export const parseSkattekontoSaldo = (rådata?: string): number | undefined => {
    if (!rådata) return undefined;
    const fält = rådata.match(/"([^"]*)"/g);
    if (!fält || fält.length === 0) return undefined;
    const sista = fält[fält.length - 1].replace(/"/g, '');
    const n = Number(sista.replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) && sista.trim() !== '' ? n : undefined;
};

// ----------------------------------------------------------------------
// SWR options
// ----------------------------------------------------------------------

export const swrOptions = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------
// Mini-body primitives used inside cards
// ----------------------------------------------------------------------

export function MiniList({ children }: { children: React.ReactNode }) {
    return (
        <Stack
            divider={
                <Box sx={(theme) => ({ borderTop: `1px dashed ${theme.palette.divider}` })} />
            }
            spacing={0}
        >
            {children}
        </Stack>
    );
}

export function MiniRow({
    primary,
    secondary,
    meta,
    trailing,
    trailingColor,
}: {
    primary: string;
    secondary?: string;
    meta?: string;
    trailing?: string;
    trailingColor?: string;
}) {
    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 0.85, minHeight: 36 }}
        >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.82rem' }}
                >
                    {primary}
                </Typography>
                {secondary && (
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem' }}
                    >
                        {secondary}
                    </Typography>
                )}
            </Box>
            {trailing ? (
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: trailingColor ?? 'text.primary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                >
                    {trailing}
                </Typography>
            ) : meta ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                >
                    {meta}
                </Typography>
            ) : null}
        </Stack>
    );
}

export function NyckeltalRad({
    etikett,
    belopp,
    framhävd,
}: {
    etikett: string;
    belopp: string;
    framhävd?: boolean;
}) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.55 }}>
            <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: '0.82rem', fontWeight: framhävd ? 700 : 500 }}
            >
                {etikett}
            </Typography>
            <Typography
                sx={{
                    fontWeight: framhävd ? 800 : 600,
                    fontSize: framhävd ? '0.9rem' : '0.82rem',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                }}
            >
                {belopp}
            </Typography>
        </Stack>
    );
}
